import { useTranslation } from "@/contexts/LanguageContext";
import {
  Camera,
  Lightning,
  Backpack,
  Lightbulb,
  Warning,
  CalendarCheck,
  Crosshair,
} from "@phosphor-icons/react";
import { motion } from "framer-motion";

const container = {
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: { staggerChildren: 0.06 } },
};
const item = {
  hidden: { opacity: 0, y: 12 },
  show: { opacity: 1, y: 0, transition: { duration: 0.3 } },
};

const SECTIONS = [
  { key: "lens", Icon: Camera, color: "text-[#F5A524]" },
  { key: "flash", Icon: Lightning, color: "text-amber-400" },
  { key: "gear", Icon: Backpack, color: "text-blue-400" },
  { key: "tips", Icon: Lightbulb, color: "text-emerald-400" },
  { key: "cautions", Icon: Warning, color: "text-red-400" },
];

export default function EventGuidance({ eventScenario, primaryGoal }) {
  const { t } = useTranslation();

  if (!eventScenario || eventScenario === "none") return null;

  const goalTip =
    primaryGoal && primaryGoal !== "none"
      ? t(`goal_${primaryGoal}_tip`)
      : null;

  return (
    <motion.div
      variants={container}
      initial="hidden"
      animate="show"
      className="space-y-3 mt-4"
      data-testid="event-guidance"
    >
      {/* Header */}
      <motion.div variants={item} className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <CalendarCheck weight="fill" className="w-4.5 h-4.5 text-[#F5A524]" />
          <h2 className="font-['Outfit'] text-xl font-semibold text-white">
            {t("eg_title")}
          </h2>
        </div>
        <span className="text-xs text-[#71717A] bg-white/5 px-2.5 py-1 rounded-md" data-testid="event-badge">
          {t(`event_${eventScenario}`)}
        </span>
      </motion.div>

      {/* Guidance sections */}
      {SECTIONS.map(({ key, Icon, color }) => (
        <motion.div
          key={key}
          variants={item}
          className="bg-[#121214]/80 backdrop-blur-md border border-white/10 rounded-xl p-4"
          data-testid={`eg-${key}`}
        >
          <div className="flex items-start gap-3">
            <Icon weight="fill" className={`w-4 h-4 mt-0.5 shrink-0 ${color}`} />
            <div>
              <h4 className="text-white font-medium text-sm mb-1">
                {t(`eg_${key}`)}
              </h4>
              <p className="text-[#A1A1AA] text-sm leading-relaxed">
                {t(`event_${eventScenario}_${key}`)}
              </p>
            </div>
          </div>
        </motion.div>
      ))}

      {/* Primary goal tip */}
      {goalTip && (
        <motion.div
          variants={item}
          className="bg-[#121214]/80 backdrop-blur-md border border-[#F5A524]/20 rounded-xl p-4"
          data-testid="eg-goal-tip"
        >
          <div className="flex items-start gap-3">
            <Crosshair weight="fill" className="w-4 h-4 mt-0.5 shrink-0 text-[#F5A524]" />
            <div>
              <h4 className="text-white font-medium text-sm mb-1">
                {t(`goal_${primaryGoal}`)}
              </h4>
              <p className="text-[#A1A1AA] text-sm leading-relaxed">{goalTip}</p>
            </div>
          </div>
        </motion.div>
      )}
    </motion.div>
  );
}
